import React, {useState} from "react"
import ReevaluateBusinessProductApplicationForm, {
	ReevaluatedBusinessProductApplicationData,
} from "../../components/ReevaluateProductApplication/Forms/ReevaluateBusinessProductApplicationForm/ReevaluateBusinessProductApplicationForm"
import {ProductApplicationResubmitForms} from "./ReevaluateProductApplicationPage"
import {useModal} from "react-modal-hook"
import ReevaluateBusinessProductApplicationConfirmationModal from "../../components/ReevaluateProductApplication/ReevaluateProductApplicationConfirmationModal/ReevaluateBusinessProductApplicationConfirmationModal"
import {useNavigate} from "react-router-dom"
import {OkDocument, ErrorDocument} from "../../resources/common"
import {BusinessProductApplication} from "../../resources/productApplication"
import ProductApplicationDetails from "./ProductApplicationDetails"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateBusinessProductApplicationProps {
	application: OkDocument<BusinessProductApplication>
}

function toBusinessProductApplicationFormData(
	businessApplication: BusinessProductApplication
): ReevaluatedBusinessProductApplicationData | undefined {
	if (
		!businessApplication.attributes.address ||
		!businessApplication.attributes.phone ||
		!businessApplication.attributes.ein
	) {
		return undefined
	}

	return {
		name: businessApplication.attributes.name,
		dba: businessApplication.attributes.dba ?? "",
		address: businessApplication.attributes.address,
		ein: businessApplication.attributes.ein,
		phone: businessApplication.attributes.phone,
		stateOfIncorporation: businessApplication.attributes.stateOfIncorporation,
		website:
			businessApplication.attributes.website === undefined || businessApplication.attributes.website === ""
				? null
				: businessApplication.attributes.website,
		businessVertical: businessApplication.attributes.businessVertical ?? undefined,
		yearOfIncorporation: businessApplication.attributes.yearOfIncorporation ?? undefined,
		numberOfEmployees: businessApplication.attributes.numberOfEmployees ?? undefined,
		annualRevenue: businessApplication.attributes.annualRevenue ?? undefined,
		cashFlow: businessApplication.attributes.cashFlow ?? undefined,
		operatingAddress: businessApplication.attributes.operatingAddress ?? undefined,
		industry: businessApplication.attributes.industry ?? undefined,
		countriesOfOperation: businessApplication.attributes.countriesOfOperation ?? undefined,
		contact: businessApplication.attributes.contact,
	}
}

function normalizeDbaValue(dba: string | undefined, application: BusinessProductApplication): string | undefined {
	const originalDba = application.attributes.dba
	return (dba === undefined || dba === "") && originalDba === undefined ? undefined : dba
}

export default function ReevaluateBusinessProductApplicationContainer({
	application,
}: ReevaluateBusinessProductApplicationProps) {
	const navigate = useNavigate()
	const businessApplicationFormData = toBusinessProductApplicationFormData(application.data)

	const [businessReevaluatedData, setBusinessReevaluatedData] = useState<
		ReevaluatedBusinessProductApplicationData | undefined
	>(businessApplicationFormData)
	const [formError, setFormError] = useState<ErrorDocument | undefined>()
	const [conversionError, setConversionError] = useState(false)

	const [showBusinessConfirmationModal, closeBusinessConfirmationModal] = useModal(() => {
		if (!businessReevaluatedData) {
			setConversionError(true)
			return <></>
		}
		return (
			<ReevaluateBusinessProductApplicationConfirmationModal
				onSuccess={() => navigate(`/product-applications/${application.data.id}`)}
				onError={(error) => {
					setFormError(error)
				}}
				close={closeBusinessConfirmationModal}
				application={application.data}
				reevaluatedData={businessReevaluatedData}
			/>
		)
	}, [businessReevaluatedData])

	const title = application.data.attributes.name
	const meta: MetaItemProps[] = [{text: "Business", icon: "toolbox"}]

	const form = (
		<ProductApplicationDetails
			title={title}
			meta={meta}
			productApplicationId={application.data.id}
			status={application.data.attributes.status}
			formId={ProductApplicationResubmitForms.businessForm}
		>
			{businessApplicationFormData && (
				<ReevaluateBusinessProductApplicationForm
					businessApplication={businessApplicationFormData}
					submitCallback={(values) => {
						setBusinessReevaluatedData({...values, dba: normalizeDbaValue(values.dba, application.data)})
						setFormError(undefined)
						showBusinessConfirmationModal()
					}}
					formId={ProductApplicationResubmitForms.businessForm}
					error={formError}
					onErrorDismiss={() => setFormError(undefined)}
					disableSensitiveFields
				/>
			)}
		</ProductApplicationDetails>
	)

	if (conversionError) {
		return (
			<Admonition
				type="is-danger"
				title="Failed to process resubmission data, please reach administrator."
				message={""}
			/>
		)
	}

	if (!businessApplicationFormData) {
		return (
			<Admonition
				type="is-danger"
				title="Could not be able to show resubmit form, please reach administator."
				message={""}
			/>
		)
	}

	return form
}
